import { colors, transition, typoV2_CSS } from "oolib";
import { css, keyframes } from "styled-components";

const { yellowHighlight, blueHighlight, pinkHighlight, white }  = colors;

// Single source of truth for the collab "locked by another user" colour.
// Used by the pulse animation, the outline on the mark, and any UI
// affordance that wants to attribute the lock (e.g. the lightbox banner).
// Helper produces an rgba string at the requested opacity so callers don't
// repeat the rgb triplet.
const LOCKED_BY_OTHER_COLOUR_RGB = "124, 58, 237";
export const lockedByOtherColor = (opacity = 1) =>
  `rgba(${LOCKED_BY_OTHER_COLOUR_RGB}, ${opacity})`;

// Pulse animation for annotations currently being edited by another user
// (collab lock indicator).
const lockedByOtherPulse = keyframes`
  0%, 100% { outline-color: ${lockedByOtherColor(1)}; }
  50%      { outline-color: ${lockedByOtherColor(0.45)}; }
`;

// export const yellowHighlight = "#FFE600";
// let blueHighlight = "#52E2EE";
// let pinkHighlight = "#FF9AF4";

export const annoThemeConfig = {

    mark: 'annoLex',
//   markOverlap: 'annoLex_overlap',

};

const commonCss = css`
    ${transition('border-color', 'background-color')};
    cursor: ${({ readOnly, disableAnnoColors }) => (readOnly && !disableAnnoColors ? "pointer" : "text")};
    border-bottom: 2px solid;
    border-color: ${colors.none};
`


export const annoThemeCSS = css`

/**
the class logic below might be a little confusing to read, so here
is a simplistic explanation of what it does:
- normal tag annotations are in yellow, opacity driven by overlap count.
- overlap is rendered additively: each annotation stacked on a fragment adds
  ~20% opacity, so overlapping annotations get progressively darker — the same
  way overlapping translucent rectangles composite. The count comes from the
  anno-depth custom property, set by AnnoPlugin's mutation listener
  (utils/applyClassesForAnnoColor.js); the OPACITY MATH lives here in CSS, not
  in JS. color-mix clamps past 100%, so depth 5+ is just solid yellow.
- normal yellow annos when hovered also turn to a 75% opacity + underline in blue
- now toggle tag annotations have their own unique colors. for now, quotes are pink & vocabs are blue
- these toggle tag annos when hovered only get a blue underline, no bg color change
- when ANY of these above annos are selected, they are set to a standard blue bg styling
*/
.annoLex {
    ${commonCss}
    background-color: ${({ disableAnnoColors }) => disableAnnoColors ? `transparent` : `color-mix(in srgb, ${yellowHighlight} calc(20% * var(--anno-depth, 1)), transparent)`} ;
    ${({ disableAnnoColors }) => disableAnnoColors ? `color: inherit;` : ``}
    padding: 0; //normalize native css

    /* ---- FILTER DIMMING ----
       When a filter is applied, AnnoPlugin tags every mark with -anno-filtered-
       and reports, per fragment, how many of the annotations covering it are
       INCLUDED in the filter (--anno-active) vs EXCLUDED (--anno-dim).

       Instead of a flat grey wash, each fragment composites two layers the way
       overlapping translucent rectangles do:
         - a grey layer — the host's theme grey (e.g. V3's var(--grey-10), a
           ~10% translucent grey), threaded in by AnnoPlugin as --anno-dim-color
           and set only when ≥1 covering anno is filtered out — painted as the
           background-color (bottom). The shared plugin has no page palette, so
           the colour comes from the host (setDimmedAnnos's color arg) and resolves
           on the mark via CSS-var inheritance from the host's theme root.
         - a yellow layer (25% per included anno) — painted as a background-image
           gradient on top.
       So where an included annotation overlaps an excluded one (e.g. B ⊂ A with
       only B in focus) the shared run reads as yellow-25% mixed with the grey,
       NOT double-strength yellow. The opacity + compositing stay in CSS; JS only
       supplies the counts + dim colour. Overrides below (-is-*, -hovered-,
       -selected-) reset background-image so an active state masks the dim
       composite cleanly. */
    &.-anno-filtered-{
        background-color: ${({ disableAnnoColors }) => disableAnnoColors ? `transparent` : `var(--anno-dim-color, transparent)`} ;
        background-image: ${({ disableAnnoColors }) => disableAnnoColors ? `none` : `linear-gradient(color-mix(in srgb, ${yellowHighlight} calc(25% * var(--anno-active, 0)), transparent) 0%, color-mix(in srgb, ${yellowHighlight} calc(25% * var(--anno-active, 0)), transparent) 100%)`} ;
    }

    &.-is-quotes-{
        background-color: ${({ disableAnnoColors }) => disableAnnoColors ? `transparent` : `${pinkHighlight}`} ;
        background-image: none;
    }

    &.-is-vocabs-{
        background-color: ${({ disableAnnoColors }) => disableAnnoColors ? `transparent` : `${blueHighlight}`} ;
        background-image: none;
    }

    &.-hovered-:not(.-selected-){
        border-color: ${({ disableAnnoColors }) => disableAnnoColors ? colors.none : colors.blue};
    }

    &.-hovered-:not(.-selected-):not(.-is-vocabs-):not(.-is-quotes-){
        background-color: ${({ disableAnnoColors }) => disableAnnoColors ? `transparent` : `${yellowHighlight}75`}; //75% opacity
        background-image: none;
        border-color: ${({ disableAnnoColors }) => disableAnnoColors ? colors.none : colors.blue};
    }

    &.-selected-{
        background-color: ${({ disableAnnoColors, theme }) => disableAnnoColors ? `transparent` : theme?.colors.primaryColor40};
        background-image: none;
        border-color: ${({ disableAnnoColors }) => disableAnnoColors ? colors.none : colors.blue};
    }

    /* Collab lock indicator — another user is currently editing this anno.
       Thick dashed pulsing outline + an attribution tab attached to the
       top-left corner. The tab text comes from data-editing-by, set by
       AnnoPlugin alongside the class. Outline (not border) keeps text
       reflow stable. position: relative is needed for the ::before tab
       to anchor to the mark. */
    &.-locked-by-other-{
        position: relative;
        outline: 3px dashed ${lockedByOtherColor(1)};
        outline-offset: 2px;
        animation: ${lockedByOtherPulse} 1.4s ease-in-out infinite;
        cursor: not-allowed;
    }
    &.-locked-by-other-::before{
        /* Pulls UI_CAPTION_DF typography directly from oolib's typo theme
           so it tracks any future changes there — same typography that
           TagLink (and Tag) use. ::before can't host a React component,
           but typoV2_CSS.UI_CAPTION_DF is a styled-components css fragment,
           which composes cleanly inside another css block. */
        ${typoV2_CSS.UI_CAPTION_DF};
        content: attr(data-editing-by);
        position: absolute;
        bottom: 100%;
        left: -5px;
        margin-bottom: 4px;
        padding: 0.2rem 0.6rem;
        background-color: ${lockedByOtherColor(1)};
        color: ${white};
        border-radius: 4px 4px 0 0;
        white-space: nowrap;
        pointer-events: none;
        z-index: 1;
    }
  }
`;
